import { observer } from 'mobx-react-lite'
import { useRef, useState } from 'react'
import { Link, useHistory } from 'react-router-dom'
import { Box, Text, Button, Menu, Modal, Group } from '@mantine/core'
import { Profile } from '../../app/models/profile'
import { useStore } from '../../app/stores/store'
import { toast } from 'react-toastify'
import { AmmoScoreBadge } from '../../app/common/ui'
import FollowersModal from './FollowersModal'

const COVER_HEIGHT = 240

function getInitials (displayName?: string | null) {
  if (!displayName?.trim()) return '??'
  const parts = displayName.trim().split(/\s+/)
  if (parts.length >= 2) return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
  return displayName.slice(0, 2).toUpperCase()
}

const BTN_BASE = {
  background: '#21262D',
  border: '1.5px solid #484F58',
  color: '#E6EDF3',
  padding: '7px 14px',
  borderRadius: 5,
  fontSize: 12,
  cursor: 'pointer' as const,
  fontFamily: 'DM Sans, sans-serif'
}

interface Props {
  profile: Profile;
  canEdit?: boolean;
}

export default observer(function ProfileHeader ({ profile, canEdit }: Props) {
  const { profileStore, userStore } = useStore()
  const fileInputRef = useRef<HTMLInputElement>(null)
  const history = useHistory()
  const [followersOpened, setFollowersOpened] = useState(false)
  const [followingOpened, setFollowingOpened] = useState(false)
  const [unfollowConfirm, setUnfollowConfirm] = useState(false)

  const isCurrentUser = userStore.user?.username === profile.username
  const canFollow = !isCurrentUser && userStore.isLoggedIn
  const showAnonFollow = !isCurrentUser && !userStore.isLoggedIn
  const username = profile.username

  const handleCoverFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return
    profileStore.uploadCover(username, file).then(() => {
      toast.success('Cover image updated')
    }).catch((err: any) => {
      toast.error(err?.response?.data ?? 'Failed to upload cover')
    })
    e.target.value = ''
  }

  const doFollow = () => {
    profileStore.follow(username).then(() => toast.success(`Following @${username}`)).catch((err: any) => toast.error(err?.response?.data || 'Failed to update follow status'))
  }

  const doUnfollow = () => {
    profileStore.unfollow(username).then(() => {
      toast.success(`Unfollowed @${username}`)
      setUnfollowConfirm(false)
    }).catch((err: any) => toast.error(err?.response?.data || 'Failed to update follow status'))
  }

  const followersCount = profile.followersCount ?? 0
  const followingCount = profile.followingCount ?? 0

  return (
    <Box sx={{ marginBottom: 0 }}>
      {/* Cover section — 240px */}
      <Box
        sx={{
          position: 'relative',
          height: COVER_HEIGHT,
          background: profile.coverImageUrl
            ? `url(${profile.coverImageUrl}) center/cover no-repeat`
            : 'linear-gradient(135deg, #0D1117 0%, #1a0e05 40%, #2a1505 60%, #0D1117 100%)',
          '&::after': {
            content: '""',
            position: 'absolute',
            inset: 0,
            background: 'repeating-linear-gradient(45deg, rgba(232,93,4,0.05) 0, transparent 1px, transparent 12px)',
            pointerEvents: 'none'
          }
        }}
      >
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          style={{ display: 'none' }}
          onChange={handleCoverFileChange}
        />
        {canEdit && (
          <Box
            component="button"
            type="button"
            onClick={() => fileInputRef.current?.click()}
            sx={{
              position: 'absolute',
              top: 16,
              right: 16,
              zIndex: 2,
              background: 'rgba(13,17,23,0.75)',
              border: '1px solid #484F58',
              color: '#E6EDF3',
              padding: '5px 10px',
              borderRadius: 5,
              fontSize: 11,
              cursor: 'pointer',
              fontFamily: 'DM Sans, sans-serif'
            }}
          >
            Edit Cover
          </Box>
        )}
      </Box>

      {/* Identity section */}
      <Box
        sx={{
          padding: '0 24px',
          borderBottom: '1px solid var(--gs-border)',
          position: 'relative',
          zIndex: 2,
          '@media (max-width: 640px)': {
            padding: '0 12px'
          }
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'flex-end',
            gap: 16,
            marginTop: -60,
            marginBottom: 14
          }}
        >
          <Box
            sx={{
              width: 88,
              height: 88,
              borderRadius: '50%',
              border: '4px solid var(--gs-bg)',
              outline: '3px solid var(--gs-gold)',
              flexShrink: 0,
              background: profile.image
                ? `url(${profile.image}) center/cover`
                : 'linear-gradient(135deg, #E85D04, #C9A84C)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              position: 'relative',
              zIndex: 3,
              boxShadow: '0 0 0 2px rgba(13,17,23,0.9)'
            }}
          >
            {!profile.image && (
              <Text sx={{ fontFamily: 'Oswald, sans-serif', fontWeight: 700, fontSize: 30, color: '#fff' }}>
                {getInitials(profile.displayName)}
              </Text>
            )}
          </Box>
          <Box sx={{ flex: 1, display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between' }}>
            <Box />
            <Group spacing="xs">
              {canEdit && (
                <>
                  <Button
                    component={Link}
                    to={`/profiles/${username}/edit`}
                    sx={BTN_BASE}
                  >
                    Edit Profile
                  </Button>
                  <Menu shadow="md" width={160} position="bottom-end">
                    <Menu.Target>
                      <Box component="button" type="button" sx={{ ...BTN_BASE, padding: '7px 10px' }}>
                        ⋯
                      </Box>
                    </Menu.Target>
                    <Menu.Dropdown>
                      <Menu.Item>Report</Menu.Item>
                      <Menu.Item>Share</Menu.Item>
                    </Menu.Dropdown>
                  </Menu>
                </>
              )}
              {!canEdit && canFollow && (
                <Box
                  component="button"
                  type="button"
                  onClick={() => (profile.isFollowing ? setUnfollowConfirm(true) : doFollow())}
                  disabled={profileStore.updatingFollow}
                  sx={{
                    ...BTN_BASE,
                    ...(profile.isFollowing
                      ? { '&:hover': { borderColor: '#E85D04' } }
                      : { background: '#E85D04', borderColor: '#E85D04', color: '#fff' })
                  }}
                >
                  {profileStore.updatingFollow ? '…' : profile.isFollowing ? 'Unfollow' : 'Follow'}
                </Box>
              )}
              {showAnonFollow && (
                <Box
                  component="button"
                  type="button"
                  onClick={() => history.push('/register')}
                  sx={{ ...BTN_BASE, background: '#E85D04', borderColor: '#E85D04', color: '#fff' }}
                >
                  Follow
                </Box>
              )}
            </Group>
          </Box>
        </Box>

        <Text
          sx={{
            fontFamily: 'Oswald, sans-serif',
            fontWeight: 700,
            fontSize: 22,
            textTransform: 'uppercase',
            letterSpacing: 1.5,
            color: 'var(--gs-text)'
          }}
        >
          {profile.displayName || username}
        </Text>
        <Text sx={{ fontFamily: 'DM Sans, sans-serif', fontSize: 13, color: 'var(--gs-muted)', marginTop: 3, marginBottom: 8 }}>
          @{username}
        </Text>
        <Box sx={{ marginBottom: 8 }}>
          <AmmoScoreBadge tier={profile.ammoTier ?? 'Newcomer'} score={profile.ammoScore ?? 0} size="lg" />
        </Box>
        {profile.bio && (
          <Text sx={{ fontFamily: 'DM Sans, sans-serif', fontSize: 13, color: 'var(--gs-muted)', lineHeight: 1.6, maxWidth: 560, marginBottom: 12 }}>
            {profile.bio}
          </Text>
        )}
        <Box sx={{ display: 'flex', gap: 16, flexWrap: 'wrap', marginBottom: 14, fontSize: 12 }}>
          <Text
            component="span"
            sx={{ color: 'var(--gs-muted)', cursor: 'pointer' }}
            onClick={() => setFollowersOpened(true)}
          >
            <Text component="span" weight={600} color="var(--gs-text)">{followersCount}</Text> followers
          </Text>
          <Text
            component="span"
            sx={{ color: 'var(--gs-muted)', cursor: 'pointer' }}
            onClick={() => setFollowingOpened(true)}
          >
            <Text component="span" weight={600} color="var(--gs-text)">{followingCount}</Text> following
          </Text>
        </Box>
      </Box>

      {/* Stats bar — horizontally scrollable so it never overflows on any viewport width */}
      <Box
        sx={{
          display: 'flex',
          flexWrap: 'nowrap',
          overflowX: 'auto',
          WebkitOverflowScrolling: 'touch',
          scrollbarWidth: 'none',
          '&::-webkit-scrollbar': { display: 'none' },
          padding: '0 24px',
          borderBottom: '1px solid var(--gs-border)',
          '@media (max-width: 640px)': {
            padding: '0 12px'
          }
        }}
      >
        {[
          { num: profile.ammoScore ?? 0, label: 'AmmoScore', accent: true },
          { num: followersCount, label: 'Followers', click: () => setFollowersOpened(true) },
          { num: followingCount, label: 'Following', click: () => setFollowingOpened(true) },
          { num: profile.postCount ?? 0, label: 'Posts' },
          { num: profile.buildCount ?? 0, label: 'Builds' },
          { num: profile.wishlistCount ?? 0, label: 'Wishlist' },
          { num: profile.approvedSubmissionsCount ?? 0, label: 'Parts Approved' }
        ].map((item, i) => (
          <Box
            key={item.label}
            component={item.click ? 'button' : 'div'}
            type={item.click ? 'button' : undefined}
            onClick={item.click}
            sx={{
              // Reset button default border first, then apply separator
              border: 'none',
              background: 'none',
              borderRight: i < 6 ? '1px solid var(--gs-border)' : 'none',
              flexShrink: 0,
              padding: '14px 20px 14px 0',
              marginRight: 20,
              textAlign: 'left',
              cursor: item.click ? 'pointer' : 'default',
              '&:hover': item.click ? { '& .stat-num': { color: 'var(--gs-accent)' } } : {},
              '@media (max-width: 640px)': {
                padding: '12px 16px 12px 0',
                marginRight: 16
              }
            }}
          >
            <Text className="stat-num" sx={{ fontFamily: 'Oswald, sans-serif', fontWeight: 700, fontSize: 22, color: item.accent ? 'var(--gs-accent)' : 'var(--gs-text)' }}>
              {item.num}
            </Text>
            <Text sx={{ fontFamily: 'DM Sans, sans-serif', fontSize: 10, color: 'var(--gs-muted)', textTransform: 'uppercase', letterSpacing: 0.8 }}>
              {item.label}
            </Text>
          </Box>
        ))}
      </Box>

      <FollowersModal username={username} opened={followersOpened} onClose={() => setFollowersOpened(false)} mode="followers" />
      <FollowersModal username={username} opened={followingOpened} onClose={() => setFollowingOpened(false)} mode="following" />
      <Modal opened={unfollowConfirm} onClose={() => setUnfollowConfirm(false)} title="Unfollow?">
        <Text size="sm" mb="md">
          Unfollow @{username}? You can follow them again anytime.
        </Text>
        <Group spacing="xs">
          <Button color="red" variant="light" onClick={doUnfollow} loading={profileStore.updatingFollow}>
            Unfollow
          </Button>
          <Button variant="subtle" onClick={() => setUnfollowConfirm(false)} disabled={profileStore.updatingFollow}>
            Cancel
          </Button>
        </Group>
      </Modal>
    </Box>
  )
})
